import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import {
  Area,
  BarChart,
  Bar,
  ComposedChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { TabProps, RechartsClickData } from './types'
import { useFormatters } from '../../contexts/PreferencesContext'
import { ChartWrapper } from './ChartWrapper'
import api, { fetchAllPages } from '../../lib/api'
import { formatCompactNumber } from '../../lib/utils'
import { useDrilldownAction } from '../drilldown'
import {
  aggregateEmissions,
  type EmissionsDisplayData,
  type SupplierEmissionItem,
  type TransportBreakdownItem,
} from './emissionsAggregate'
import { AlertTriangle, Loader2 } from 'lucide-react'

// Transport mode icons and colors
const TRANSPORT_CONFIG = {
  air: { color: '#3b82f6', icon: '✈️', label: 'Air' },
  sea: { color: '#06b6d4', icon: '🚢', label: 'Sea' },
  rail: { color: '#8b5cf6', icon: '🚂', label: 'Rail' },
  road: { color: '#10b981', icon: '🚛', label: 'Road' },
}

// Supplier count selector component
function TopNSelector({
  value,
  onChange,
}: {
  value: number
  onChange: (value: number) => void
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="text-xs border rounded px-2 py-1 bg-background"
    >
      <option value={5}>Top 5</option>
      <option value={10}>Top 10</option>
      <option value={15}>Top 15</option>
    </select>
  )
}

// Transport breakdown card component
function TransportCard({
  mode,
  emissions,
  distance,
  percentage,
  total: _total,
}: {
  mode: keyof typeof TRANSPORT_CONFIG
  emissions: number
  distance: number
  percentage: number
  total: number
}) {
  const config = TRANSPORT_CONFIG[mode]

  return (
    <Card className="shadow-sm">
      <CardContent className="pt-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-2xl">{config.icon}</span>
            <span className="font-medium">{config.label}</span>
          </div>
          <div
            className="text-sm font-bold px-2 py-1 rounded"
            style={{ backgroundColor: config.color, color: 'white' }}
          >
            {percentage.toFixed(1)}%
          </div>
        </div>
        <div className="space-y-1 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Total Emission</span>
            <span className="font-semibold">{emissions.toLocaleString('en-GB', { maximumFractionDigits: 2 })} KG CO₂e</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Distance</span>
            <span className="font-semibold">{distance.toLocaleString('en-GB', { maximumFractionDigits: 0 })} KM</span>
          </div>
        </div>
        {/* Mini progress bar */}
        <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${percentage}%`, backgroundColor: config.color }}
          />
        </div>
      </CardContent>
    </Card>
  )
}

export function EmissionsTab({ data: _data, filters, dataPeriod: _dataPeriod, orderType: _orderType }: TabProps) {
  const { formatNumber } = useFormatters()
  const [topSupplierCount, setTopSupplierCount] = useState(5)
  const [emissionsData, setEmissionsData] = useState<EmissionsDisplayData | null>(null)
  const [loading, setLoading] = useState(true)
  // CR-8240: a failed fetch used to render as zeros and "No data".
  const [error, setError] = useState<string | null>(null)

  // Hook for programmatic drill-down (for chart clicks)
  const { openDrilldown } = useDrilldownAction()

  // Convert dashboard filters to drilldown API format
  const drilldownFilters = {
    ...(filters?.dateFrom && { date_from: filters.dateFrom }),
    ...(filters?.dateTo && { date_to: filters.dateTo }),
    ...(filters?.supplier && { supplier: filters.supplier }),
    ...(filters?.customer && { customer: filters.customer }),
    ...(filters?.site && { site: filters.site }),
    // CR-7229: carry the warehouse scope into the drill-downs so the detail
    // list matches the tile it was opened from.
    ...(filters?.warehouse && { warehouse: filters.warehouse }),
    ...(filters?.siteCountry && { site_country: filters.siteCountry }),
    ...(filters?.supplierGroup && { supplier_group: filters.supplierGroup }),
    ...(filters?.supplierCountry && { supplier_country: filters.supplierCountry }),
  }

  // Helper function to parse month string to date range
  const parseMonthToDateRange = (monthStr: string): { date_from: string; date_to: string } | null => {
    try {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      const parts = monthStr.trim().split(' ')
      const monthIndex = monthNames.indexOf(parts[0])
      if (monthIndex === -1) return null
      let year: number
      if (parts.length >= 2 && parts[1]) {
        year = parseInt(parts[1])
        if (year < 100) year += 2000
      } else {
        year = new Date().getFullYear()
      }
      // CR-4671: Format in local timezone — see dashboardUtils.ts for context.
      const startDate = new Date(year, monthIndex, 1)
      const endDate = new Date(year, monthIndex + 1, 0)
      const fmt = (d: Date): string =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      return {
        date_from: fmt(startDate),
        date_to: fmt(endDate),
      }
    } catch {
      return null
    }
  }

  // Handle chart click - open ASN list for the selected period
  const handleChartClick = (chartData: RechartsClickData | null) => {
    if (!chartData) return
    let month: string | null = null
    if (chartData.activePayload && chartData.activePayload[0]) {
      month = (chartData.activePayload[0].payload?.['month'] as string) || null
    } else if (chartData.activeLabel) {
      month = chartData.activeLabel
    }
    if (!month) return

    // Parse month to date range for API filtering
    const dateRange = parseMonthToDateRange(month)
    const chartFilters = dateRange ? {
      ...drilldownFilters,
      date_from: dateRange.date_from,
      date_to: dateRange.date_to,
    } : drilldownFilters

    // Open spend drilldown as emissions are related to ASNs/shipments
    openDrilldown('spend', chartFilters, month)
  }

  // Fetch emissions data from ASNs
  useEffect(() => {
    // CR-8240: the period now narrows the ASN list, so a slower response for a
    // window the user has already left must not overwrite the current one.
    let stale = false
    const fetchEmissionsData = async () => {
      try {
        setLoading(true)
        setError(null)
        // Walk pages — the backend clamps page_size to 100, so every CO2e
        // aggregate below was computed from at most one page of ASNs.
        const asns = await fetchAllPages<Record<string, unknown>>(
          (page) => api.get('/purchasing/asns/', {
            params: {
              page_size: 100,
              page,
              ...(filters?.dateFrom && { shipment_date_after: filters.dateFrom }),
              ...(filters?.dateTo && { shipment_date_before: filters.dateTo }),
              ...(filters?.supplier && { supplier: filters.supplier }),
            }
          }),
        )

        if (stale) return
        setEmissionsData(aggregateEmissions(asns))
      } catch (err) {
        if (stale) return
        console.error('Error fetching emissions data:', err)
        setError('Emissions data could not be loaded. Please refresh to try again.')
      } finally {
        if (!stale) setLoading(false)
      }
    }

    fetchEmissionsData()
    return () => { stale = true }
  }, [filters?.dateFrom, filters?.dateTo, filters?.supplier])

  const emptyData: EmissionsDisplayData = {
    total: 0,
    byTransport: [],
    bySupplier: [],
    byMonth: [],
    trend: [],
  }

  const displayData = emissionsData || emptyData
  const transportBreakdown = displayData.byTransport || []
  const topSuppliers = (displayData.bySupplier || []).slice(0, topSupplierCount)

  // Pie chart colors
  const PIE_COLORS = ['#3b82f6', '#06b6d4', '#8b5cf6', '#10b981']

  return (
    // CR-8240: a period change keeps the last figures on screen, dimmed and
    // marked busy, until the new period's arrive.
    <div className={`space-y-4 transition-opacity ${loading && emissionsData ? 'opacity-60' : ''}`} aria-busy={loading}>
      {/* Header */}
      <div className="bg-card rounded-lg shadow-sm p-4">
        <h2 className="text-lg font-semibold">CO₂ Emissions Overview</h2>
        <p className="text-sm text-muted-foreground">
          Carbon emissions calculated from ASN shipment data
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2 text-sm text-red-800" role="alert">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      {/* CR-8240: no figures until the first fetch lands — zeros here read as
          "this period had no emissions". */}
      {!error && !emissionsData && (
        <div className="flex items-center justify-center h-40 bg-card rounded-lg shadow-sm">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label="Loading" />
        </div>
      )}

      {!error && emissionsData && (<>
      {/* Total Carbon Emissions Breakdown */}
      <Card className="shadow-sm bg-gradient-to-r from-green-50 to-blue-50 border-green-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Total Carbon Emissions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="text-3xl font-bold text-green-700">
              {formatNumber(displayData.total)} KG CO₂e
            </div>
            <div className="grid grid-cols-4 gap-4 text-sm">
              {transportBreakdown.map((t: TransportBreakdownItem) => (
                <div key={t.mode} className="text-center">
                  <div className="font-medium" style={{ color: TRANSPORT_CONFIG[t.mode as keyof typeof TRANSPORT_CONFIG]?.color }}>
                    {TRANSPORT_CONFIG[t.mode as keyof typeof TRANSPORT_CONFIG]?.label}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {t.emissions.toLocaleString('en-GB', { maximumFractionDigits: 2 })} KG
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Row - Pie Chart and Transport Cards */}
      <div className="grid grid-cols-3 gap-4">
        {/* Main Pie Chart */}
        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Emissions by Transport Mode</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={transportBreakdown}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={90}
                  dataKey="emissions"
                  nameKey="mode"
                  label={(props: Record<string, unknown>) => `${TRANSPORT_CONFIG[props['mode'] as keyof typeof TRANSPORT_CONFIG]?.label}: ${(props['percentage'] as number)?.toFixed(1)}%`}
                  labelLine={{ stroke: '#888', strokeWidth: 1 }}
                >
                  {transportBreakdown.map((entry: TransportBreakdownItem, index: number) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={TRANSPORT_CONFIG[entry.mode as keyof typeof TRANSPORT_CONFIG]?.color || PIE_COLORS[index]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number, name: string) => [
                    `${value.toLocaleString('en-GB', { maximumFractionDigits: 2 })} KG CO₂e`,
                    TRANSPORT_CONFIG[name as keyof typeof TRANSPORT_CONFIG]?.label || name
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Transport Cards Grid */}
        <div className="col-span-2 grid grid-cols-2 gap-4">
          {transportBreakdown.map((t: TransportBreakdownItem) => (
            <TransportCard
              key={t.mode}
              mode={t.mode as keyof typeof TRANSPORT_CONFIG}
              emissions={t.emissions}
              distance={t.distance}
              percentage={t.percentage}
              total={displayData.total}
            />
          ))}
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-2 gap-4">
        {/* Impact Over Time */}
        <ChartWrapper
          title="Impact Over Time"
          data={displayData.trend || []}
          exportFilename="emissions-impact-over-time"
          legend={[
            { color: '#10b981', label: 'Aggregation' },
            { color: '#ef4444', label: 'Trend Line' },
          ]}
        >
          {/* CR-8240: ComposedChart, not LineChart — a LineChart silently drops
              <Area>, so only the trend line ever drew, never the emissions. */}
          <ResponsiveContainer width="100%" height={250}>
            <ComposedChart
              data={displayData.trend || []}
              onClick={handleChartClick}
              style={{ cursor: 'pointer' }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={formatCompactNumber} />
              <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} formatter={(value: number) => [`${value.toLocaleString('en-GB', { maximumFractionDigits: 2 })} KG CO₂e`]} />
              <Area
                type="monotone"
                dataKey="emissions"
                stroke="#10b981"
                fill="#bbf7d0"
                // A one-month period has no area to fill; the dot keeps it visible.
                dot={{ r: 3, fill: '#10b981', strokeWidth: 0 }}
                name="Aggregation"
              />
              {/* One month has no trend, and its point would sit on the emissions dot. */}
              {(displayData.trend || []).length > 1 && (
                <Line
                  type="monotone"
                  dataKey="trend_line"
                  stroke="#ef4444"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={false}
                  name="Trend Line"
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </ChartWrapper>

        {/* Top CO₂e Contributing Suppliers */}
        <ChartWrapper
          title="Top CO₂e Contributing Suppliers"
          data={topSuppliers}
          exportFilename="emissions-by-supplier"
          headerActions={
            <TopNSelector value={topSupplierCount} onChange={setTopSupplierCount} />
          }
        >
          <ResponsiveContainer width="100%" height={250}>
            <PieChart
              onClick={() => openDrilldown('spend', drilldownFilters)}
              style={{ cursor: 'pointer' }}
            >
              <Pie
                data={topSuppliers}
                cx="50%"
                cy="50%"
                innerRadius={40}
                outerRadius={80}
                dataKey="emissions"
                nameKey="supplier"
                label={(props: Record<string, unknown>) => `${props['supplier']}: ${(props['percentage'] as number)?.toFixed(1)}%`}
              >
                {topSuppliers.map((_: SupplierEmissionItem, index: number) => (
                  <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: number, name: string, props: { payload?: Record<string, unknown> }) => [
                  `${value.toLocaleString('en-GB', { maximumFractionDigits: 2 })} KG CO₂e`,
                  (props.payload?.['fullName'] as string) || name
                ]}
              />
            </PieChart>
          </ResponsiveContainer>
        </ChartWrapper>
      </div>

      {/* Carbon Emissions by Method of Transport (Stacked Bar) */}
      <ChartWrapper
        title="Carbon Emissions by Method of Transport"
        data={displayData.byMonth || []}
        exportFilename="emissions-by-transport-monthly"
        legend={[
          { color: TRANSPORT_CONFIG.air.color, label: 'Air' },
          { color: TRANSPORT_CONFIG.sea.color, label: 'Sea' },
          { color: TRANSPORT_CONFIG.rail.color, label: 'Rail' },
          { color: TRANSPORT_CONFIG.road.color, label: 'Road' },
        ]}
      >
        <ResponsiveContainer width="100%" height={280}>
          <BarChart
            data={displayData.byMonth || []}
            onClick={handleChartClick}
            style={{ cursor: 'pointer' }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis dataKey="month" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={formatCompactNumber} />
            <Tooltip
              cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }}
              formatter={(value: number, name: string) => [
                `${value.toLocaleString('en-GB', { maximumFractionDigits: 2 })} KG CO₂e`,
                TRANSPORT_CONFIG[name as keyof typeof TRANSPORT_CONFIG]?.label || name
              ]}
            />
            <Bar dataKey="air" stackId="a" fill={TRANSPORT_CONFIG.air.color} name="air" />
            <Bar dataKey="sea" stackId="a" fill={TRANSPORT_CONFIG.sea.color} name="sea" />
            <Bar dataKey="rail" stackId="a" fill={TRANSPORT_CONFIG.rail.color} name="rail" />
            <Bar dataKey="road" stackId="a" fill={TRANSPORT_CONFIG.road.color} name="road" />
          </BarChart>
        </ResponsiveContainer>
      </ChartWrapper>

      </>)}

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> Emissions data is calculated based on ASN shipment information using standard emission factors:
          Road (0.213), Rail (0.022), Air (0.602), Sea (0.016) kg CO₂ per tonne-km.
          {loading && ' Loading latest data...'}
        </p>
      </div>
    </div>
  )
}
